import {api, RequestParam} from "@/shared/api";

import {BasicDataTable} from "@/shared/ui/basicDataTable";
import {
	CalendarEventSource,
	FilterParamSource,
	WorkingTimeRange,
	WorkingTimeRequestsBase,
	WorkingTimeRequestsBaseConfig
} from "./workingTimeRequestsBase";
import {SecurityStatus} from "@/shared/securityStatus";
import {Combobox} from "@/shared/ui/combobox";
import {GroupDropdown} from "@/shared/ui/groupDropdown";
import {WorkingTimeRequestApprovalDialog} from "@/features/requests/workingTimeRequestApprovalDialog";
import {WorkingTimeRequestTableSmall} from "@/features/requests/workingTimeRequestTableSmall";
import {MenuEntryList} from "@/shared/ui/contextMenu";
import * as Constants from "@/shared/constants";
import {WorkingTimeRequestUtil} from "@/shared/workingtime/workingTimeRequestUtil";
import {statusElementsHolder} from "@/shared/recording/statusElementsHolder";
import {BrowserStorageSettings} from "@/shared/browserStorageSettings";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {DOMUtil} from "@/shared/utils/dom";
import {
	AbstractWorkingTimeRequestResponseModel,
	User,
	WorkingTimeDateSpan,
	WorkingTimeNotice,
	WorkingTimeType
} from "@/shared/timr";
import {WorkingTimeRequestEvent, WorkingTimeTypeRecordingMode} from "@/shared/timrTypes";
import {WorkingTimeNoticeDialogBridge} from "@/features/requests/workingTimeNoticeDialog/workingTimeNoticeDialogBridge";
import {FullCalendarSelection} from "@/shared/fullCalendarSelectionContextMenu";
import {getISODate, momentToPlainDate, parseISODate, Temporal} from "@/shared/utils/datefunctions";

export interface WorkingTimeRequestsApprovalConfig extends WorkingTimeRequestsBaseConfig {
	users: User[];
	allowedUserIds: number[];
	workingTimeTypesForNotices: WorkingTimeType[];
}

export class WorkingTimeRequestsApprovalPage extends WorkingTimeRequestsBase {

	private userCombobox: Combobox<User>;
	private groupDropdown: GroupDropdown;
	private workingTimeRequestApprovalDialog: WorkingTimeRequestApprovalDialog;

	constructor(public config: WorkingTimeRequestsApprovalConfig) {
		super(
			"/timr/requests/findWorkingTimeRequestsForApproval.json",
			"/timr/requests/findWorkingTimeRequestsForApprovalCalendar.json",
			"/timr/requests/findWorkingTimesWithoutDateSpan.json",
			"/timr/workingTimeDateSpans/findWorkingTimeDateSpansWithoutRequest.json",
			"/timr/requests/findHolidays.json",
			true,
			true,
			config,
			"asc"
		);

		this.init();
	}

	protected init() {
		super.init();

		this.initCalendar();
		this.initWorkingTimeRequestApprovalDialog();

		DOMUtil.fillWorkingTimeTypeSelect($("#workingTimeType"),
			this.config.workingTimeTypes.filter(wtt => wtt.recordingModeUser === WorkingTimeTypeRecordingMode.BY_REQUEST),
			["", messages["filter.all"]]
		);

		$(this.workingTimeRequestTable).on(WorkingTimeRequestTableSmall.EVENT_EDIT, (e, workingTimeRequest: AbstractWorkingTimeRequestResponseModel) => {
			this.workingTimeRequestApprovalDialog.edit(workingTimeRequest);
		}).on(WorkingTimeRequestTableSmall.EVENT_SHOW, (e, workingTimeRequest: AbstractWorkingTimeRequestResponseModel) => {
			this.workingTimeRequestApprovalDialog.show(workingTimeRequest);
		});

		$(this.workingTimeRequestTable).on(BasicDataTable.EVENT_DBLCLICK, (e, workingTimeRequest: AbstractWorkingTimeRequestResponseModel) => {
			this.showOrEditRequest(workingTimeRequest);
		});

		$(".addAbsenceButton").click(() => {
			this.addAbsence();
		}).toggleClass("disabled", this.getWorkingTimeTypesForAddAbsence().length === 0);
	}

	protected override initWorkingTimeNoticeDialog() {
		this.workingTimeNoticeDialog = new WorkingTimeNoticeDialogBridge({
			workingTimeTypes: this.config.workingTimeTypesForNotices,
			showUserRow: globals.isGroupLeader,
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself
		});
		$(this.workingTimeNoticeDialog).on(WorkingTimeNoticeDialogBridge.EVENT_NOTICE_SAVED, () => {
			this.reload();
		}).on(WorkingTimeNoticeDialogBridge.EVENT_NOTICE_DELETED, () => {
			this.reload();
		});

		$("#addNoticeButton").click(() => {
			this.workingTimeNoticeDialog.openForCreate();
		});
	}

	protected createContextMenuItems(): MenuEntryList<AbstractWorkingTimeRequestResponseModel> {
		return [{
			label: messages["requests.workingTimeRequestsUser.context.edit"],
			action: (workingTimeRequest) => this.editWorkingTimeRequest(workingTimeRequest),
			active: (workingTimeRequest) => WorkingTimeRequestUtil.isInApprovalProcess(workingTimeRequest) ||
				WorkingTimeRequestUtil.isInCancellationProcess(workingTimeRequest),
		}, "divider", {
			label: messages["requests.workingTimeRequestsUser.context.approve"],
			action: (workingTimeRequest) => this.approveWorkingTimeRequest(workingTimeRequest),
			active: (workingTimeRequest) => WorkingTimeRequestUtil.isInApprovalProcess(workingTimeRequest)
		}, {
			label: messages["requests.workingTimeRequestsUser.context.reject"],
			action: (workingTimeRequest) => this.rejectWorkingTimeRequest(workingTimeRequest),
			active: (workingTimeRequest) => WorkingTimeRequestUtil.isInApprovalProcess(workingTimeRequest),
		}, {
			label: messages["requests.workingTimeRequestsUser.context.approveCancellation"],
			action: (workingTimeRequest) => this.approveCancellationWorkingTimeRequest(workingTimeRequest),
			active: (workingTimeRequest) => WorkingTimeRequestUtil.isInCancellationProcess(workingTimeRequest),
		}, {
			label: messages["requests.workingTimeRequestsUser.context.rejectCancellation"],
			action: (workingTimeRequest) => this.rejectCancellationWorkingTimeRequest(workingTimeRequest),
			active: (workingTimeRequest) => WorkingTimeRequestUtil.isInCancellationProcess(workingTimeRequest),
		}];
	}

	private showOrEditRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		if (WorkingTimeRequestUtil.isInApprovalProcess(workingTimeRequest) || WorkingTimeRequestUtil.isInCancellationProcess(workingTimeRequest)) {
			this.workingTimeRequestApprovalDialog.edit(workingTimeRequest);
		} else {
			this.workingTimeRequestApprovalDialog.show(workingTimeRequest);
		}
	}

	private editWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		this.showOrEditRequest(workingTimeRequest);
	}

	private approveWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		BootstrapModal.confirmPrompt({
			title: "workingTimeRequestStatus.approve.title",
			message: messages["workingTimeRequestStatus.approve.message"],
			yesLabel: "confirmation.approve",
			yesCss: "btn-success",
			noLabel: "confirmation.cancel",
			noCss: "btn-default",
			promptNameTag: "statusComment",
			css: {
				header: "bg-success"
			},
			yesAction: (modal: BootstrapModal.Modal) => {
				api(WorkingTimeRequestUtil.SEND_WORKING_TIME_REQUEST_EVENT_URL)
					.post({
						workingTimeRequestId: workingTimeRequest.id,
						workingTimeRequestEvent: WorkingTimeRequestEvent.APPROVE_REQUEST_STEP,
						workingTimeRequestStepId: workingTimeRequest.currentApprovableStep?.workingTimeRequestStepId,
						comment: modal.getReason()
					})
					.done(() => {
						modal.close();
						statusElementsHolder.updateRequestsApproval();
						this.reload();
					}).fail((response) => {
					WorkingTimeRequestUtil.handleConfirmPromptFail(response, modal);
				});
			}
		});
	}

	private rejectWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		BootstrapModal.confirmPrompt({
			title: "workingTimeRequestStatus.reject.title",
			message: messages["workingTimeRequestStatus.reject.message"],
			yesLabel: "confirmation.reject",
			yesCss: "btn-danger",
			noLabel: "confirmation.cancel",
			noCss: "btn-default",
			promptNameTag: "statusComment",
			css: {
				header: "bg-danger"
			},
			yesAction: (modal: BootstrapModal.Modal) => {
				api(WorkingTimeRequestUtil.SEND_WORKING_TIME_REQUEST_EVENT_URL)
					.post({
						workingTimeRequestId: workingTimeRequest.id,
						workingTimeRequestEvent: WorkingTimeRequestEvent.REJECT_REQUEST_STEP,
						workingTimeRequestStepId: workingTimeRequest.currentApprovableStep?.workingTimeRequestStepId,
						comment: modal.getReason()
					})
					.done(() => {
						modal.close();
						statusElementsHolder.updateRequestsApproval();
						this.reload();
					}).fail((response) => {
					WorkingTimeRequestUtil.handleConfirmPromptFail(response, modal);
				});
			}
		});
	}

	private approveCancellationWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		BootstrapModal.confirmPrompt({
			title: "workingTimeRequestStatus.approveCancellation.title",
			message: messages["workingTimeRequestStatus.approveCancellation.message"],
			yesLabel: "confirmation.approveCancellation",
			yesCss: "btn-success",
			noLabel: "confirmation.cancel",
			noCss: "btn-default",
			promptNameTag: "statusComment",
			css: {
				header: "bg-success"
			},
			yesAction: (modal: BootstrapModal.Modal) => {
				api(WorkingTimeRequestUtil.SEND_WORKING_TIME_REQUEST_EVENT_URL)
					.post({
						workingTimeRequestId: workingTimeRequest.id,
						workingTimeRequestEvent: WorkingTimeRequestEvent.APPROVE_CANCELLATION_STEP,
						workingTimeRequestStepId: workingTimeRequest.currentApprovableStep?.workingTimeRequestStepId,
						comment: modal.getReason()
					})
					.done(() => {
						modal.close();
						statusElementsHolder.updateRequestsApproval();
						this.reload();
					}).fail((response) => {
					WorkingTimeRequestUtil.handleConfirmPromptFail(response, modal);
				});
			}
		});
	}

	private rejectCancellationWorkingTimeRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel) {
		BootstrapModal.confirmPrompt({
			title: "workingTimeRequestStatus.rejectCancellation.title",
			message: messages["workingTimeRequestStatus.rejectCancellation.message"],
			yesLabel: "confirmation.rejectCancellation",
			yesCss: "btn-danger",
			noLabel: "confirmation.cancel",
			noCss: "btn-default",
			promptNameTag: "statusComment",
			css: {
				header: "bg-danger"
			},
			yesAction: (modal: BootstrapModal.Modal) => {
				api(WorkingTimeRequestUtil.SEND_WORKING_TIME_REQUEST_EVENT_URL)
					.post({
						workingTimeRequestId: workingTimeRequest.id,
						workingTimeRequestEvent: WorkingTimeRequestEvent.REJECT_CANCELLATION_STEP,
						workingTimeRequestStepId: workingTimeRequest.currentApprovableStep?.workingTimeRequestStepId,
						comment: modal.getReason()
					})
					.done(() => {
						modal.close();
						statusElementsHolder.updateRequestsApproval();
						this.reload();
					}).fail((response) => {
					WorkingTimeRequestUtil.handleConfirmPromptFail(response, modal);
				});
			}
		});
	}

	private initCalendar() {
		this.workingTimeRequestCalendar.fullCalendar("option", "eventClick", (event: FullCalendar.EventObject, jsEvent: MouseEvent) => {
			const element = $(jsEvent.currentTarget);
			if (element.hasClass("disabled") || element.hasClass("fc-event-disabled")) {
				return;
			}
			if (event.className.indexOf(WorkingTimeRequestsBase.EVENT_CLASS_NOTICE) !== -1) {
				this.workingTimeNoticeDialog.openForEdit(event.data as WorkingTimeNotice);
				jsEvent.stopPropagation();
			} else if (event.className.indexOf(WorkingTimeRequestsBase.EVENT_CLASS_WORKING_TIME_REQUEST) !== -1) {
				const workingTimeRequest = event.data as AbstractWorkingTimeRequestResponseModel;
				if (workingTimeRequest) {
					this.showOrEditRequest(workingTimeRequest);
					jsEvent.stopPropagation();
				}
			}
		});
	}

	protected buildCalendarEventSources(): CalendarEventSource[] {
		const eventSources = super.buildCalendarEventSources();
		if (SecurityStatus.isWorkingTimeNoticesAllowed()) {
			eventSources.push({
				events: (start, end, _timezone, callback) => {
					this.loadCalendarNotices(momentToPlainDate(start), momentToPlainDate(end), callback);
				}
			});
		}
		return eventSources;
	}

	private loadCalendarNotices(start: Temporal.PlainDate, end: Temporal.PlainDate, callback: (events: FullCalendar.EventObject[]) => void) {
		const params: Array<{ name: string; value: unknown }> = [
			{name: "from", value: getISODate(start)},
			{name: "to", value: getISODate(end)}
		];
		const selectedGroupId = this.groupDropdown?.getSelectedId();
		if (selectedGroupId) {
			params.push({name: "groupId", value: selectedGroupId});
		}
		const selectedUserId = this.userCombobox?.getSelectedItem()?.id;
		if (selectedUserId) {
			params.push({name: "userId", value: selectedUserId});
		}

		void api("notices/findCalendarEntriesForApproval.json")
			.get(params)
			.done((response: WorkingTimeNotice[]) => {
				const events: FullCalendar.EventObject[] = response.map(notice => ({
					title: this.buildCalendarTitleNotice(notice),
					start: notice.start,
					end: getISODate(parseISODate(notice.end)?.add({days: 1})),
					allDay: true,
					className: [
						WorkingTimeRequestsApprovalPage.EVENT_CLASS_NOTICE,
						"request-notice",
						"request-category-" + notice.workingTimeTypeCategory
					],
					data: notice
				}));
				callback(events);
			});
	}

	private initWorkingTimeRequestApprovalDialog() {
		this.workingTimeRequestApprovalDialog = new WorkingTimeRequestApprovalDialog({
			dialogId: "#workingTimeRequestApprovalDialog",
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			customFieldDefinitions: this.config.customFieldDefinitions
		});
		$(this.workingTimeRequestApprovalDialog).on(WorkingTimeRequestApprovalDialog.EVENT_REQUEST_APPROVED, () => {
			this.reload();
		}).on(WorkingTimeRequestApprovalDialog.EVENT_REQUEST_REJECTED, () => {
			this.reload();
		});
	}

	protected initFilter() {
		super.initFilter();

		$("#workingTimeType").change(() => {
			this.reload();
		});

		$("#showInactive").change(() => {
			this.reloadUserList();
			this.reload();
		});
		this.userCombobox = new Combobox({
			items: this.config.users,
			formatter: (user) => {
				return user.fullname;
			},
			idElement: $("#userId"),
			filterElement: $("#userFilter"),
			caretElement: $("#userCaret")
		});
		this.userCombobox.setSelectedItemById(BrowserStorageSettings.getReportUserFilter(Constants.GROUP_REPORT_FILTER));
		$(this.userCombobox).on(Combobox.EVENT_CHANGED, (e, data: User | undefined) => {
			this.reload();
			BrowserStorageSettings.setReportUserFilter(Constants.GROUP_REPORT_FILTER, data?.id);
		});
		this.groupDropdown = new GroupDropdown({
			rootElement: $("#groupDropdown"),
			allowSelectionOfNotAssigned: false
		});
		$(this.groupDropdown).on(GroupDropdown.EVENT_SELECTED, (e, group) => {
			this.reloadUserList();
			this.reload();
		});
	}

	protected buildFilterParams(filterParamSource: FilterParamSource): RequestParam[] {
		const params = super.buildFilterParams(filterParamSource);
		params.push({name: "workingTimeTypeId", value: $("#workingTimeType").val() as string});
		params.push({name: "groupId", value: this.groupDropdown.getSelectedId()});
		const user = this.userCombobox.getSelectedItem();
		params.push({name: "userId", value: user?.id});
		params.push({name: "activeUsersOnly", value: !$("#showInactive").is(":checked")});
		if (filterParamSource === FilterParamSource.Table) {
			params.push({
				name: "statuses", value: WorkingTimeRequestUtil.UNFINISHED_STATUSES.join(",")
			});
		} else {
			params.push({
				name: "statuses", value: WorkingTimeRequestUtil.ACTIVE_STATUSES.join(",")
			});
		}
		return params;
	}

	protected buildCalendarTitleRequest(workingTimeRequest: AbstractWorkingTimeRequestResponseModel): string {
		return workingTimeRequest.user.fullname + ": " + workingTimeRequest.workingTimeType.name;
	}

	protected buildCalendarTitleWorkingTime(workingTimeRange: WorkingTimeRange): string {
		return workingTimeRange.user.fullname + ": " + workingTimeRange.workingTimeType.name;
	}

	protected buildCalendarTitleWorkingTimeDateSpan(workingTimeDateSpan: WorkingTimeDateSpan): string {
		return `${workingTimeDateSpan.user.fullname}: ${workingTimeDateSpan.workingTimeType.name}`;
	}

	protected override buildCalendarTitleNotice(notice: WorkingTimeNotice): string {
		return `${notice.userFullname}: ${notice.workingTimeTypeName}`;
	}

	private buildUserListParams(): RequestParam[] {
		return [
			{name: "activeUsersOnly", value: !$("#showInactive").is(":checked")},
			{name: "groupId", value: this.groupDropdown.getSelectedId()},
		];
	}

	private reloadUserList() {
		api("reports/filterWorkingTimeRequestUsers.json")
			.post(this.buildUserListParams())
			.done(data => {
				this.userCombobox.updateItems(data.users || []);
			});
	}

	protected addAbsence(start: Temporal.ZonedDateTime | null = null, end: Temporal.ZonedDateTime | null = null) {
		super.addAbsence(start, end);

		const workingTimeTypes = this.getWorkingTimeTypesForAddAbsence();
		this.workingTimeDialog.create({
			workingTimeTypeId: null,
			userId: this.userCombobox.getSelectedItem()?.id
		}, false, workingTimeTypes);
		this.workingTimeDialog.setStartEndTime(start, end);
	}

	protected override addNotice(selection: FullCalendarSelection) {
		const selectedUser = this.userCombobox?.getSelectedItem();

		this.workingTimeNoticeDialog?.openForCreate({
			selectedUser: (
				selectedUser ? {
					id: selectedUser.id,
					fullname: selectedUser.fullname
				} : undefined
			),
			start: selection.start,
			end: selection.end,
		});
	}

	protected setUsers(users: User[]) {
		this.userCombobox.updateItems(users);
	}
}
